import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { RegisterPayload, Session } from '../types';
import { authService } from '../services/authService';

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  updateProfile: (payload: { name?: string; email?: string; phone?: string; password?: string }) => Promise<void>;
  logout: () => void;
}

const STORAGE_KEY = 'premium_session';
const AuthContext = createContext<AuthContextValue | null>(null);

const readSession = (): Session | null => {
  const fromService = authService.getSession();
  if (fromService) return fromService;
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? (JSON.parse(raw) as Session) : null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(readSession);
  const [loading, setLoading] = useState(false);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const next = await authService.login(email, password);
      setSession(next);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload: RegisterPayload) => {
    setLoading(true);
    try {
      const next = await authService.register(payload);
      setSession(next);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setSession(null);
    authService.clearSession();
    localStorage.removeItem(STORAGE_KEY);
  };

  const updateProfile: AuthContextValue['updateProfile'] = async (payload) => {
    if (!session) throw new Error('Sessão inválida.');
    setLoading(true);
    try {
      const next = await authService.updateProfile(session.user.id, payload);
      setSession(next);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({
      session,
      loading,
      login,
      register,
      updateProfile,
      logout
    }),
    [session, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
